import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EventListFilters } from 'src/types/events';

type EventStatusValue = 'active' | 'inactive';

export type EventsDrawerFilterDraft = {
  startDate: Date | null;
  endDate: Date | null;
  status: EventStatusValue | '';
};

export type EventsDrawerApplyPayload = {
  mappedFilters: EventListFilters;
  draftFilters: EventsDrawerFilterDraft;
};

type UseEventsDrawerFiltersParams = {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (payload: EventsDrawerApplyPayload) => void;
  appliedFilters: EventsDrawerFilterDraft | null;
};

const createEmptyDraft = (): EventsDrawerFilterDraft => ({
  startDate: null,
  endDate: null,
  status: '',
});

const normalizeDateInput = (value: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    const isValidDate =
      parsed.getFullYear() === Number(year) &&
      parsed.getMonth() === Number(month) - 1 &&
      parsed.getDate() === Number(day);

    return isValidDate ? parsed : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeDateRange = (
  startDateInput: Date | string | null,
  endDateInput: Date | string | null
) => {
  const startDate = normalizeDateInput(startDateInput);
  const endDate = normalizeDateInput(endDateInput);

  if (!startDate && !endDate) {
    return { startDate: null, endDate: null };
  }

  const safeStartDate = startDate || endDate;
  const safeEndDate = endDate || startDate;

  if (!safeStartDate || !safeEndDate) {
    return { startDate: null, endDate: null };
  }

  return safeEndDate < safeStartDate
    ? { startDate: safeStartDate, endDate: new Date(safeStartDate) }
    : { startDate: safeStartDate, endDate: safeEndDate };
};

const cloneDraft = (draft: EventsDrawerFilterDraft): EventsDrawerFilterDraft => {
  const dateRange = normalizeDateRange(draft.startDate, draft.endDate);

  return {
    ...draft,
    ...dateRange,
  };
};

export const restoreEventsDrawerFilterDraft = (
  draft: EventsDrawerFilterDraft | null | undefined
): EventsDrawerFilterDraft | null => (draft ? cloneDraft(draft) : null);

export const restoreEventsDrawerFilterDraftFromMappedFilters = (
  filters: EventListFilters | null | undefined
): EventsDrawerFilterDraft | null => {
  if (!filters?.length) {
    return null;
  }

  const restored = createEmptyDraft();

  filters.forEach((filter) => {
    if (filter.column === 'startDate' && typeof filter.value === 'string') {
      restored.startDate = normalizeDateInput(filter.value);
    }

    if (filter.column === 'endDate' && typeof filter.value === 'string') {
      restored.endDate = normalizeDateInput(filter.value);
    }

    if (filter.column === 'status' && typeof filter.value === 'string') {
      restored.status = ['active', 'inactive'].includes(filter.value)
        ? (filter.value as EventStatusValue)
        : '';
    }
  });

  return cloneDraft(restored);
};

const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const mapEventsDrawerFilters = (draft: EventsDrawerFilterDraft): EventListFilters => {
  const mapped: EventListFilters = [];
  const { startDate, endDate } = normalizeDateRange(draft.startDate, draft.endDate);

  if (startDate && endDate) {
    mapped.push({ column: 'startDate', value: formatDateForApi(startDate) });
    mapped.push({ column: 'endDate', value: formatDateForApi(endDate) });
  }

  if (draft.status) {
    mapped.push({ column: 'status', value: draft.status });
  }

  return mapped;
};

export default function useEventsDrawerFilters({
  isOpen,
  onClose,
  onApplyFilters,
  appliedFilters,
}: UseEventsDrawerFiltersParams) {
  const [draftFilters, setDraftFilters] = useState<EventsDrawerFilterDraft>(
    () => restoreEventsDrawerFilterDraft(appliedFilters) || createEmptyDraft()
  );
  const { control, reset, setValue } = useForm<
    Pick<EventsDrawerFilterDraft, 'startDate' | 'endDate'>
  >({
    defaultValues: {
      startDate: null,
      endDate: null,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const cloned = restoreEventsDrawerFilterDraft(appliedFilters) || createEmptyDraft();
    setDraftFilters(cloned);
    reset({ startDate: cloned.startDate, endDate: cloned.endDate });
  }, [appliedFilters, isOpen, reset]);

  const handleChangeDate = useCallback(
    (field: 'startDate' | 'endDate', value: Date | null) => {
      if (!value) {
        setDraftFilters((current) => ({ ...current, [field]: null }));
        setValue(field, null, { shouldDirty: true });
        return;
      }

      const normalized = normalizeDateRange(
        field === 'startDate' ? value : draftFilters.startDate,
        field === 'endDate' ? value : draftFilters.endDate
      );
      setDraftFilters((current) => ({ ...current, ...normalized }));
      setValue('startDate', normalized.startDate, { shouldDirty: true });
      setValue('endDate', normalized.endDate, { shouldDirty: true });
    },
    [draftFilters.endDate, draftFilters.startDate, setValue]
  );

  const handleChangeStatus = useCallback((status: EventStatusValue | '') => {
    setDraftFilters((current) => ({ ...current, status }));
  }, []);

  const handleApply = useCallback(() => {
    const normalizedDraft = cloneDraft(draftFilters);
    onApplyFilters({
      mappedFilters: mapEventsDrawerFilters(normalizedDraft),
      draftFilters: normalizedDraft,
    });
    onClose();
  }, [draftFilters, onApplyFilters, onClose]);

  const handleClear = useCallback(() => {
    const empty = createEmptyDraft();
    setDraftFilters(empty);
    reset({ startDate: null, endDate: null });
    onApplyFilters({ mappedFilters: [], draftFilters: empty });
  }, [onApplyFilters, reset]);

  return {
    control,
    draftFilters,
    statusOptions: [
      { label: 'Ativo', value: 'active' as const },
      { label: 'Inativo', value: 'inactive' as const },
    ],
    handleChangeDate,
    handleChangeStatus,
    handleApply,
    handleClear,
  };
}
